import gsap from 'gsap';
import type { RefObject } from 'react';
import { fadeFromTo, fadeTo } from 'util/animation/fade/fade';

interface SitePreloaderRefs {
  elementRef?: RefObject<HTMLElement | null>;
  progressBarRef?: RefObject<HTMLDivElement | null>;
  lottieHolderRef?: RefObject<HTMLDivElement | null>;
  skipFadeOut?: boolean;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  { elementRef, lottieHolderRef }: SitePreloaderRefs,
): void {
  if (elementRef) {
    timeline.add(fadeFromTo(elementRef.current as HTMLElement, { from: 1, duration: 0.01 }));
  }

  if (lottieHolderRef) {
    timeline.fromTo(
      lottieHolderRef.current,
      { scale: 1.3 },
      { scale: 1, duration: 0.3, ease: 'Power3.easeOut' },
      0,
    );
    timeline.add(fadeFromTo(lottieHolderRef.current as HTMLElement, { duration: 0.3 }), 0);
  }
}

export function setupTransitionOutTimeline(
  timeline: gsap.core.Timeline,
  { elementRef, skipFadeOut, progressBarRef, lottieHolderRef }: SitePreloaderRefs,
): void {
  if (progressBarRef) {
    timeline.to(
      progressBarRef.current,
      {
        scaleX: 0,
        duration: 0.5,
        ease: 'Power3.easeOut',
        transformOrigin: 'right center',
      },
      0.1,
    );
  }

  if (lottieHolderRef) {
    timeline.to(lottieHolderRef.current, { scale: 1.5, duration: 0.3, ease: 'Power3.easeIn' }, 0);
    timeline.to(lottieHolderRef.current, { scale: 0, duration: 0.5, ease: 'Power3.easeOut' }, 0.3);
  }

  if (elementRef) {
    // Do a different transition-out when we LAND on home than other pages.
    if (skipFadeOut) {
      timeline.add(fadeTo(elementRef.current as HTMLElement, { duration: 1, to: 1 }), 0);
    } else {
      const clipFrom = `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`;
      const from = {
        webkitClipPath: clipFrom,
        clipPath: clipFrom,
      };
      timeline.set(elementRef.current, from, 0);

      const clipTo = `polygon(0% 50%, 100% 50%, 100% 50%, 0% 50%)`;
      const to = {
        webkitClipPath: clipTo,
        clipPath: clipTo,
        ease: 'Power3.easeOut',
        duration: 0.8,
      };
      timeline.to(elementRef.current, to, 0);
    }
  }
}

export function setProgressBar(progress: number, { progressBarRef }: SitePreloaderRefs): void {
  if (progressBarRef) {
    gsap.set(progressBarRef.current, { scaleX: progress, transformOrigin: 'left center' });
  }
}
